import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { School, Plus, X, Loader2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown } from 'lucide-react';
import api from '@/utils/api';
import { apiRequest } from '@/utils/api-request';
import { showToast, ToastManager } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface City {
  value: number;
  label: string;
  country_id?: number;
}

interface Area {
  areaId: number;
  areaName: string;
  areaSlug: string;
  city_id_fk: number;
}

interface CreateSchoolForm {
  school_name: string;
  city_id: number | null;
  area_id: number | null;
  email: string;
  phone: string;
}

interface SchoolData {
  slate_id: number;
  slate_name: string;
  slate_slug: string;
  slate_slug_1: string;
  city: number;
  city_name: string;
  area_id_fk: number;
  area_name: string;
  verified: number;
  claimed: number;
  is_client: number;
  views: number;
  date_created: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export default function SchoolsAdmin() {
  const navigate = useNavigate();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number>(1); // 1=India, 2=UAE
  const [allCities, setAllCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [areaSearch, setAreaSearch] = useState('');
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const areaDropdownRef = useRef<HTMLDivElement>(null);
  const [citySearch, setCitySearch] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const cityDropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<CreateSchoolForm>({
    school_name: '',
    city_id: null,
    area_id: null,
    email: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Schools list state
  const [schools, setSchools] = useState<SchoolData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 25,
  });
  const [sortColumn, setSortColumn] = useState('date_created');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  // Filter states
  const [filterVerified, setFilterVerified] = useState<boolean | null>(null);
  const [filterClaimed, setFilterClaimed] = useState<boolean | null>(null);
  const [filterIsClient, setFilterIsClient] = useState<boolean | null>(null);
  const [filterPopular, setFilterPopular] = useState<boolean | null>(null);

  // Load cities on mount
  useEffect(() => {
    loadCities();
  }, []);

  // Re-filter cities when country changes
  useEffect(() => {
    setCities(allCities.filter(c => (c.country_id ?? 1) === selectedCountry));
    setFormData(prev => ({ ...prev, city_id: null, area_id: null }));
    setCitySearch('');
    setAreaSearch('');
    setAreas([]);
  }, [selectedCountry, allCities]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(event.target as Node)) {
        setIsAreaDropdownOpen(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter cities based on search
  const filteredCities = cities.filter(city =>
    city.label.toLowerCase().includes(citySearch.toLowerCase())
  );

  // Filter areas based on search
  const filteredAreas = areas.filter(area =>
    area.areaName.toLowerCase().includes(areaSearch.toLowerCase())
  );

  // Fetch schools
  const fetchSchools = async (page: number = pagination.currentPage) => {
    try {
      setIsLoading(true);
      const response = await apiRequest('/api/schools', {
        method: 'POST',
        body: JSON.stringify({
          page,
          limit: pagination.itemsPerPage,
          search_term: searchTerm,
          sort_column: sortColumn,
          sort_order: sortDirection,
          verified: filterVerified !== null ? (filterVerified ? 1 : 0) : '',
          claimed: filterClaimed !== null ? (filterClaimed ? 1 : 0) : '',
          is_client: filterIsClient !== null ? (filterIsClient ? 1 : 0) : '',
          popular: filterPopular !== null ? (filterPopular ? 1 : 0) : '',
        }),
      });

      if (response.success) {
        setSchools(response.data.schools || []);
        setPagination(response.data.pagination);
      } else {
        showToast('Failed to fetch schools', 'error');
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
      showToast('Failed to fetch schools', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Load schools on mount and when filters change
  useEffect(() => {
    fetchSchools(1);
  }, [searchTerm, sortColumn, sortDirection, filterVerified, filterClaimed, filterIsClient, filterPopular]);

  const loadCities = async () => {
    setLoadingCities(true);
    try {
      const response = await api.get<{ cities: City[] }>('/schools/cities');
      if (response.success && response.data?.cities) {
        setAllCities(response.data.cities);
        setCities(response.data.cities.filter(c => (c.country_id ?? 1) === selectedCountry));
      }
    } catch (error) {
      console.error('Failed to load cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadAreas = async (cityId: number) => {
    setLoadingAreas(true);
    setAreas([]);
    try {
      const response = await api.get<{ areas: Area[] }>(`/schools/areas/${cityId}`);
      if (response.success && response.data?.areas) {
        setAreas(response.data.areas);
      }
    } catch (error) {
      console.error('Failed to load areas:', error);
    } finally {
      setLoadingAreas(false);
    }
  };

  const selectCity = (city: City) => {
    setFormData(prev => ({
      ...prev,
      city_id: city.value,
      area_id: null,
    }));
    setCitySearch(city.label);
    setIsCityDropdownOpen(false);
    setAreaSearch('');
    setIsAreaDropdownOpen(false);
    loadAreas(city.value);
  };

  const selectArea = (area: Area) => {
    setFormData(prev => ({
      ...prev,
      area_id: area.areaId,
    }));
    setAreaSearch(area.areaName);
    setIsAreaDropdownOpen(false);
  };

  const handleInputChange = (field: keyof CreateSchoolForm, value: string | number | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      school_name: '',
      city_id: null,
      area_id: null,
      email: '',
      phone: '',
    });
    setFormErrors({});
    setAreas([]);
    setCitySearch('');
    setIsCityDropdownOpen(false);
    setAreaSearch('');
    setIsAreaDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const errors: Record<string, string> = {};

    if (!formData.school_name.trim()) {
      errors.school_name = 'School name is required';
    }
    if (!formData.city_id) {
      errors.city_id = 'Please select a city';
    }
    if (!formData.area_id) {
      errors.area_id = 'Please select an area';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setIsCreating(true);

    try {
      const response = await api.req<{
        slate_id: number;
        slate_slug: string;
        crm_client_id?: number;
        access_code?: string;
        crm_error?: string;
        message?: string;
      }>({
        url: '/schools/create',
        method: api.Method.POST,
        body: formData,
      });

      if (response.success && response.data?.slate_id) {
        if (response.data.crm_error) {
          showToast(`School created but CRM setup failed: ${response.data.crm_error}`, 'warning');
        } else {
          showToast('School and CRM account created successfully!', 'success');
        }

        setIsModalOpen(false);
        resetForm();
        navigate(`/manage-school/${response.data.slate_id}/basic`);
      } else {
        const errorMsg = response.message || response.data?.message || 'Failed to create school';
        showToast(errorMsg, 'error');
      }
    } catch (error: any) {
      console.error('Failed to create school:', error);
      showToast(error.message || 'Failed to create school', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSchoolClick = (school: SchoolData) => {
    navigate(`/manage-school/${school.slate_id}`);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchSchools(newPage);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortColumn(column);
      setSortDirection('DESC');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      <ToastManager />
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
        <div className="flex items-center gap-4">
          {/* Left Side - Icon, Title with Badge */}
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <School className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Schools Management</h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                  {pagination.totalItems.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage all school profiles and information</p>
            </div>
          </div>

          {/* Right Side - Search, Filters, Add Button */}
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search schools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Add School Button */}
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors whitespace-nowrap"
            >
              <Plus className="h-4 w-4" />
              Add School
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-4 mt-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterPopular === true}
              onChange={(e) => setFilterPopular(e.target.checked ? true : null)}
              className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Popular</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterVerified === true}
              onChange={(e) => setFilterVerified(e.target.checked ? true : null)}
              className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Verified</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterClaimed === true}
              onChange={(e) => setFilterClaimed(e.target.checked ? true : null)}
              className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Claimed</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterIsClient === true}
              onChange={(e) => setFilterIsClient(e.target.checked ? true : null)}
              className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Clients</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-4 mt-4">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-brandColor text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('slate_name')}
                >
                  School Name {sortColumn === 'slate_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Slug</th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('city')}
                >
                  City {sortColumn === 'city' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Area</th>
                <th
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('verified')}
                >
                  Verified {sortColumn === 'verified' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('claimed')}
                >
                  Claimed {sortColumn === 'claimed' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('is_client')}
                >
                  Client {sortColumn === 'is_client' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('views')}
                >
                  Views {sortColumn === 'views' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                  onClick={() => handleSort('date_created')}
                >
                  Created {sortColumn === 'date_created' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading schools...
                  </td>
                </tr>
              ) : schools.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No schools found
                  </td>
                </tr>
              ) : (
                schools.map((school, index) => (
                  <tr
                    key={school.slate_id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}`}
                  >
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{school.slate_id}</td>
                    <td className="px-4 py-2 text-sm">
                      <button
                        onClick={() => handleSchoolClick(school)}
                        className="text-gray-800 dark:text-gray-200 hover:text-brandColor font-semibold text-left hover:underline"
                      >
                        {school.slate_name}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                      {school.slate_slug || school.slate_slug_1 || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{school.city_name || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{school.area_name || '-'}</td>
                    <td className="px-4 py-2 text-sm text-center">
                      {school.verified === 1 ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Yes</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-center">
                      {school.claimed === 1 ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Yes</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-center">
                      {school.is_client === 1 ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">Yes</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-gray-600 dark:text-gray-400">
                      {school.views?.toLocaleString() || 0}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(school.date_created).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
          {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
          {pagination.totalItems} results
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1}>
            <ChevronsLeft size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}>
            <ChevronRight size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages}>
            <ChevronsRight size={16} />
          </Button>
        </div>
      </div>

      {/* Add School Slider */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <div
            onClick={() => { setIsModalOpen(false); resetForm(); }}
            className="fixed inset-0 bg-black/70 transition-opacity duration-300 cursor-pointer"
          />

          {/* Slider Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-gray-800 shadow-2xl transition-transform duration-300 ease-out flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 bg-brandColor flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <School className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Add New School</h3>
              </div>
              <button
                onClick={() => { setIsModalOpen(false); resetForm(); }}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Body */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  School Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.school_name}
                  onChange={(e) => {
                    handleInputChange('school_name', e.target.value);
                    if (formErrors.school_name) setFormErrors(prev => ({ ...prev, school_name: '' }));
                  }}
                  placeholder="Enter school name"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white ${formErrors.school_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {formErrors.school_name && <p className="mt-1 text-xs text-red-500">{formErrors.school_name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden mb-3">
                  {[{ label: '🇮🇳 India', value: 1 }, { label: '🇦🇪 UAE', value: 2 }].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSelectedCountry(opt.value)}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${selectedCountry === opt.value ? 'bg-brandColor text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={cityDropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => {
                        setCitySearch(e.target.value);
                        setFormData(prev => ({ ...prev, city_id: null, area_id: null }));
                        setIsCityDropdownOpen(true);
                        setAreas([]);
                        setAreaSearch('');
                        if (formErrors.city_id) setFormErrors(prev => ({ ...prev, city_id: '' }));
                      }}
                      onFocus={() => setIsCityDropdownOpen(true)}
                      placeholder={loadingCities ? 'Loading cities...' : 'Search and select a city'}
                      disabled={loadingCities}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white disabled:opacity-50 ${formErrors.city_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    />
                    <ChevronDown
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${isCityDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                  {isCityDropdownOpen && !loadingCities && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCities.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          No cities found
                        </div>
                      ) : (
                        filteredCities.map((city) => (
                          <button
                            key={city.value}
                            type="button"
                            onClick={() => selectCity(city)}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                              formData.city_id === city.value ? 'bg-brandColor/10 text-brandColor font-medium' : 'text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {city.label}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {formErrors.city_id && <p className="mt-1 text-xs text-red-500">{formErrors.city_id}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Area <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={areaDropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      value={areaSearch}
                      onChange={(e) => {
                        setAreaSearch(e.target.value);
                        setFormData(prev => ({ ...prev, area_id: null }));
                        setIsAreaDropdownOpen(true);
                        if (formErrors.area_id) setFormErrors(prev => ({ ...prev, area_id: '' }));
                      }}
                      onFocus={() => formData.city_id && setIsAreaDropdownOpen(true)}
                      placeholder={
                        loadingAreas ? 'Loading areas...' : !formData.city_id ? 'Select a city first' : 'Search and select an area'
                      }
                      disabled={!formData.city_id || loadingAreas}
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white disabled:opacity-50 ${formErrors.area_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    />
                    <ChevronDown
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${isAreaDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                  {isAreaDropdownOpen && formData.city_id && !loadingAreas && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredAreas.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          No areas found
                        </div>
                      ) : (
                        filteredAreas.map((area) => (
                          <button
                            key={area.areaId}
                            type="button"
                            onClick={() => selectArea(area)}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                              formData.area_id === area.areaId ? 'bg-brandColor/10 text-brandColor font-medium' : 'text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {area.areaName}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {formErrors.area_id && <p className="mt-1 text-xs text-red-500">{formErrors.area_id}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    handleInputChange('email', e.target.value);
                    if (formErrors.email) setFormErrors(prev => ({ ...prev, email: '' }));
                  }}
                  placeholder="school@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white ${formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {formErrors.email ? (
                  <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This will be the CRM admin login email</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="9876543210 (optional)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  A CRM account will be created with password: <strong>Welcome@123</strong>
                </p>
              </div>
              </div>

              {/* Sticky Footer */}
              <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create School
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
